<script setup lang="ts">
import { computed, ref, useId } from 'vue';
import { UiFileUpload, UiText, useApi } from '@shopexperts/shared';
import type { UiFileUploadItem } from '@shopexperts/shared/dist/types/ui/UiFileUpload.vue';
import UiSortableImageList from '@/app/components/customer-stories/UiSortableImageList.vue';

const props = defineProps<{ modelValue: string; disabled?: boolean }>();
const emit = defineEmits<{ 'update:modelValue': [value: string]; busy: [value: boolean] }>();
const api = useApi();
const uploadName = `review-background-presets-${useId()}`;
const pendingUploads = ref(0);
const error = ref('');
const items = computed<UiFileUploadItem[]>({
    get() {
        try { return JSON.parse(props.modelValue).map((url: string, index: number) => ({ url, filename: `Preset ${index + 1}` })); }
        catch { return []; }
    },
    set(value) {
        emit('update:modelValue', JSON.stringify(value.filter(item => item.url).map(item => item.url), null, 2));
    },
});

async function upload(file: File) {
    pendingUploads.value++;
    emit('busy', true);
    error.value = '';
    try {
        const bitmap = await createImageBitmap(file);
        const tooLarge = bitmap.width > 4000 || bitmap.height > 4000;
        bitmap.close();
        if (tooLarge) throw new Error('Images must be no larger than 4000 × 4000 pixels.');
        return await api.media.upload({ file, collection: 'review_share_background' });
    } catch (cause) {
        error.value = (cause as { response?: { data?: { message?: string } } })?.response?.data?.message
            || (cause instanceof Error ? cause.message : 'Upload failed. Please try again.');
        throw cause;
    } finally {
        pendingUploads.value--;
        emit('busy', pendingUploads.value > 0);
    }
}
</script>

<template>
    <div class="flex flex-col gap-4">
        <UiFileUpload v-model="items" :name="uploadName" :max-files="50" :show-thumbnails="false"
            primary-text="Upload backgrounds"
            secondary-text="JPG, PNG, or WebP · Up to 10 MiB each · Maximum 4000 × 4000 pixels · Recommended 16:9"
            accept="image/jpeg,image/png,image/webp" :max-file-size="10 * 1024 * 1024"
            aspect-ratio="16:9" collection="review_share_background" :upload-fn="upload" auto-upload
            :refresh-interval="0" :disabled="disabled || pendingUploads > 0" :error="error" :invalid="!!error" />
        <UiText v-if="pendingUploads" as="p" variant="p-small" role="status" :text="`Uploading ${pendingUploads} image${pendingUploads === 1 ? '' : 's'}…`" />
        <div :inert="disabled || pendingUploads > 0">
            <UiSortableImageList v-model="items" title="Review backgrounds" @delete="item => items = items.filter(other => other.url !== item.url)" />
        </div>
        <UiText as="p" variant="caption-normal" class="text-se-gray-600" text="Drag images to change their order. Save changes to publish your presets. Removing a preset does not delete its image file." />
    </div>
</template>
